import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CreditCard, CheckCircle2, AlertCircle, ExternalLink, Crown, Users, HardDrive, Activity, ArrowUp, XCircle, Sparkles, Lock, Globe2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { redirectExternal } from '../../lib/redirect';
import { RESIDENCY_FULL } from '../../lib/residency';
import ManualOpenModal from '../../components/ManualOpenModal';
import { safeConfirm } from '../../lib/confirm';

// Iter 113o (Epic 2) — refreshed tier taglines to match the new
// competitive positioning in core_tenancy.PLAN_QUOTAS.
const PLAN_META = {
  solo:   { label: 'Solo',         desc: 'Everything a solo practitioner needs — 3 seats · 5 GB · AI Scribe · AI Dx · online booking.',   color: '#2A5C4E' },
  medium: { label: 'Practice',     desc: 'Growing practice — 15 seats · 25 GB · white-label · priority support · marketing automation.', color: '#003D80' },
  multi:  { label: 'Multi-clinic', desc: 'Multi-site chains — unlimited seats · 100 GB · API · SSO · HIPAA BAA · dedicated CSM.',        color: '#7A1F3D' },
};

export default function AdminBilling() {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [trust, setTrust] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [manualUrl, setManualUrl] = useState(null);   // Iter 126 — fallback modal URL
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const [{ data }, usageResp, trustResp] = await Promise.all([
        api.get('/clinic/me/subscription'),
        api.get('/clinic/me/usage').catch(() => ({ data: null })),
        api.get('/clinic/me/privacy-first').catch(() => ({ data: null })),
      ]);
      setData(data);
      setUsage(usageResp.data);
      setTrust(trustResp.data);
      setBillingCycle(data.billing || 'monthly');
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      if (e?.response?.status === 403) {
        setData({ forbidden: true, reason: msg });
      } else {
        toast.error('Failed to load: ' + msg);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // Handle return from Stripe Checkout success URL
    const sid = params.get('session_id');
    const cancelled = params.get('cancelled');
    if (sid) {
      toast.success('Subscription activated — Stripe sync in progress.');
      params.delete('session_id'); setParams(params, { replace: true });
      setTimeout(load, 2500);
    } else if (cancelled) {
      toast.info('Checkout cancelled.');
      params.delete('cancelled'); setParams(params, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter 123 — DRY helper for the two Stripe redirect flows (checkout + portal).
  // Both share the same shape: POST → expect a URL field → break out of iframe.
  // Iter 126 — if `redirectExternal` returns false (sandboxed iframe blocked
  // both `window.top` and `_blank`), show a manual-open modal instead of
  // silently navigating the iframe into a URL Stripe will refuse to frame.
  const startStripeFlow = async (endpoint, payload, urlField) => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, payload);
      const url = data?.[urlField];
      if (!url) {
        toast.error(`Stripe did not return a ${urlField}. Please try again or contact support.`);
        return;
      }
      const ok = redirectExternal(url);
      if (!ok) setManualUrl(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const subscribe = (plan) => startStripeFlow(
    '/clinic/me/subscription/checkout',
    { plan, billing: billingCycle, origin_url: window.location.origin },
    'checkout_url',
  );

  const openPortal = () => startStripeFlow(
    '/clinic/me/subscription/portal', {}, 'portal_url',
  );
  const cancelSub = async () => {
    const reason = window.prompt(
      "Sorry to see you go. Help us improve — what's the main reason for cancelling? (Optional)\n\nYour subscription will end at your current billing period and you keep access until then.",
      ''
    );
    if (reason === null) return; // user clicked Cancel on the prompt
    if (!await safeConfirm("Are you sure you want to cancel? You'll keep access until your current period ends, then your clinic will be flipped to a free/canceled state.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete('/clinic/me/subscription', { data: { immediate: false, reason: reason || null } });
      toast.success(data.message || 'Subscription scheduled for cancellation.');
      setTimeout(load, 2500); // give the webhook a moment to land
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  if (data?.forbidden) return (
    <div className="bento-card max-w-xl mx-auto mt-12" data-testid="billing-forbidden">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-6 h-6 text-red-600 mt-1"/>
        <div>
          <h2 className="font-heading font-semibold mb-2">Owner access only</h2>
          <p className="text-sm text-fg-muted">{data.reason}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-billing">
      <header className="flex items-center gap-3">
        <CreditCard className="w-6 h-6 text-brand"/>
        <div>
          <h1 className="font-heading text-2xl font-bold">Billing & Subscription</h1>
          <p className="text-sm text-fg-muted">Manage your clinic's plan, view invoices, update payment method.</p>
        </div>
      </header>

      {/* Current status card */}
      <div className="bento-card" data-testid="billing-current">
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <div className="text-xs text-fg-muted uppercase tracking-wider">Current plan</div>
            <div className="flex items-center gap-2 mt-1">
              <Crown className="w-4 h-4" style={{ color: PLAN_META[data?.plan]?.color }}/>
              <span className="text-xl font-bold">{PLAN_META[data?.plan]?.label || data?.plan}</span>
              <span className="text-xs text-fg-muted">({data?.billing})</span>
            </div>
          </div>
          <div>
            <div className="text-xs text-fg-muted uppercase tracking-wider">Status</div>
            <div className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
              data?.subscription_status === 'active'   ? 'bg-green-100 text-green-800' :
              data?.subscription_status === 'trialing' ? 'bg-amber-100 text-amber-800' :
              data?.subscription_status === 'past_due' ? 'bg-red-100 text-red-800' :
              data?.subscription_status === 'canceled' ? 'bg-gray-200 text-gray-800' :
              'bg-gray-100 text-gray-700'
            }`} data-testid="billing-status">
              {data?.subscription_status === 'active' && <CheckCircle2 className="w-3 h-3"/>}
              {data?.subscription_status}
            </div>
          </div>
          {data?.trial_ends_at && (
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wider">Trial ends</div>
              <div className="mt-1 text-sm">{new Date(data.trial_ends_at).toLocaleDateString()}</div>
            </div>
          )}
          {data?.stripe_current_period_end && (
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wider">Renews</div>
              <div className="mt-1 text-sm">{new Date(data.stripe_current_period_end).toLocaleDateString()}</div>
            </div>
          )}
          {data?.has_active_subscription && (
            <div className="ml-auto flex gap-2">
              <button onClick={openPortal} disabled={busy}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
                      data-testid="billing-portal-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ExternalLink className="w-4 h-4"/>}
                Manage in Stripe
              </button>
              <button onClick={cancelSub} disabled={busy}
                      className="px-3 py-2 border border-red-300 text-red-700 rounded-md text-sm font-semibold hover:bg-red-50 disabled:opacity-40 inline-flex items-center gap-2"
                      title="Cancel subscription at end of current billing period"
                      data-testid="billing-cancel-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <XCircle className="w-4 h-4"/>}
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Iter 88 — Usage card (self-monitor + self-upsell) */}
      {usage && (
        <UsageCard usage={usage}
                    onUpgrade={() => document.getElementById('plan-picker')?.scrollIntoView({ behavior: 'smooth' })}/>
      )}

      {/* Iter 95 — Privacy-First scorecard */}
      {trust && <PrivacyFirstScorecard trust={trust}/>}

      {/* Iter 99 — Anonymised peer benchmarks */}
      <BenchmarksCard/>

      {/* Plan picker */}
      <div className="bento-card" id="plan-picker">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-heading font-semibold">{data?.has_active_subscription ? 'Switch plan' : 'Pick a plan'}</h2>
          <div className="inline-flex rounded-md border border-soft overflow-hidden text-xs" data-testid="billing-cycle-toggle">
            {['monthly', 'yearly'].map((c) => (
              <button key={c} onClick={() => setBillingCycle(c)}
                      className={`px-3 py-1.5 ${billingCycle === c ? 'bg-brand text-white' : 'text-fg-muted'}`}
                      data-testid={`billing-cycle-toggle-${c}`}>
                {c === 'monthly' ? 'Monthly' : 'Yearly (save 20%)'}
              </button>
            ))}
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {Object.keys(PLAN_META).map((p) => {
            const meta = PLAN_META[p];
            const isCurrent = data?.plan === p && data?.subscription_status === 'active';
            const available = data?.available_prices?.[p]?.[billingCycle];
            return (
              <div key={p} className={`border rounded-lg p-4 transition-all ${isCurrent ? 'border-brand ring-2 ring-brand/30' : 'border-soft'}`}
                   data-testid={`plan-card-${p}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Crown className="w-4 h-4" style={{ color: meta.color }}/>
                  <h3 className="font-heading font-semibold">{meta.label}</h3>
                </div>
                <p className="text-xs text-fg-muted min-h-[2.5rem]">{meta.desc}</p>
                <button onClick={() => subscribe(p)} disabled={busy || isCurrent || !available}
                        className={`mt-3 w-full py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isCurrent ? 'bg-bg-secondary text-fg-muted' : 'bg-brand text-white hover:bg-brand/90'}`}
                        data-testid={`plan-select-${p}`}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin inline"/> :
                   isCurrent ? 'Current plan' :
                   !available ? 'Price not configured' :
                   data?.has_active_subscription ? `Switch to ${meta.label}` : `Subscribe to ${meta.label}`}
                </button>
              </div>
            );
          })}
        </div>
        {!Object.values(data?.available_prices || {}).some(c => c.monthly || c.yearly) && (
          <div className="mt-4 p-3 bg-amber-50 border-l-4 border-amber-400 text-xs rounded" data-testid="billing-no-prices">
            <strong>Stripe pricing not yet configured.</strong> The platform admin needs to set
            <code className="mx-1 bg-amber-100 px-1 rounded">STRIPE_PRICE_SOLO_MONTHLY</code> etc. in the backend env.
            Contact <a href="mailto:support@physiogenic.com" className="underline">support@physiogenic.com</a> to upgrade in the meantime.
          </div>
        )}
      </div>

      {/* Iter 126 — fallback modal when the iframe blocks redirectExternal. */}
      <ManualOpenModal url={manualUrl} onClose={() => setManualUrl(null)} testIdPrefix="billing-open"/>
    </div>
  );
}


// Iter 88 — Self-serve usage card with self-upsell CTA when ≥ 80%.
function UsageCard({ usage, onUpgrade }) {
  const u = usage.usage;
  const seatPct = Math.round(u.seat_pct * 100);
  const storagePct = Math.round(u.storage_pct * 100);
  const overall = Math.round(u.max_pct * 100);
  const approaching = u.approaching_limit;
  const over = u.over_limit;
  const storageMb = Math.round(u.storage_bytes / 1024 / 1024 * 10) / 10;
  const storageMaxGb = Math.round(u.storage_max_bytes / 1024 / 1024 / 1024);
  const sparkMax = Math.max(...usage.mau_breakdown.map(d => d.count), 1);
  return (
    <div className={`bento-card ${over ? 'border-2 border-red-300' : approaching ? 'border-2 border-amber-300' : ''}`}
         data-testid="usage-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-heading font-semibold flex items-center gap-2">
          <Activity className="w-4 h-4 text-brand"/>
          Plan usage
          {approaching && (
            <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
              over ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
            }`} data-testid="usage-warning-badge">
              {over ? `OVER LIMIT (${overall}%)` : `APPROACHING LIMIT (${overall}%)`}
            </span>
          )}
        </h2>
        {approaching && usage.next_tier && (
          <button onClick={onUpgrade}
                  className="btn-primary inline-flex items-center gap-2 text-sm"
                  data-testid="usage-upgrade-cta">
            <ArrowUp className="w-4 h-4"/>
            Upgrade to {usage.next_tier === 'medium' ? 'Medium' : 'Multi'}
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-5">
        {/* Seats */}
        <UsageMetric icon={Users}
                      label="Team seats"
                      used={u.seats_used}
                      max={u.seats_max}
                      pct={seatPct}
                      testid="usage-seats"/>
        {/* Storage */}
        <UsageMetric icon={HardDrive}
                      label="Storage"
                      used={`${storageMb} MB`}
                      max={`${storageMaxGb} GB`}
                      pct={storagePct}
                      testid="usage-storage"/>
        {/* MAU */}
        <div data-testid="usage-mau">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-fg-muted uppercase tracking-wider flex items-center gap-1">
              <Activity className="w-3 h-3"/> Active users (30d)
            </span>
            <span className="text-sm font-bold tabular-nums">{usage.mau_30d}</span>
          </div>
          {usage.mau_breakdown.length > 0 ? (
            <MauSparkline breakdown={usage.mau_breakdown} sparkMax={sparkMax}/>
          ) : <div className="h-10 flex items-center text-[11px] text-fg-muted">No activity yet.</div>}
        </div>
      </div>

      {approaching && (
        <p className="mt-4 text-xs text-fg-muted" data-testid="usage-warning-text">
          {over
            ? `You've exceeded your ${usage.plan} plan's limits. Upgrading now keeps everything working — no service interruption.`
            : `You're at ${overall}% of your ${usage.plan} plan. Consider upgrading before you hit the wall.`}
        </p>
      )}

      {usage.top_features && usage.top_features.length > 0 && (
        <div className="mt-5 pt-4 border-t border-soft" data-testid="usage-top-features">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-brand"/>
            <span className="text-xs uppercase tracking-wider text-fg-muted font-semibold">Most-used features (30d)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {usage.top_features.map((f, i) => (
              <div key={f.feature_key}
                   className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bg-secondary/60 border border-soft text-xs"
                   data-testid={`usage-feature-${f.feature_key}`}>
                <span className="font-semibold">{f.label}</span>
                <span className="text-fg-muted tabular-nums">· {f.count}</span>
                {i === 0 && <span className="text-[9px] text-amber-700 ml-0.5">🏆</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UsageMetric({ icon: Icon, label, used, max, pct, testid }) {
  const colorClass = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-brand';
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-fg-muted uppercase tracking-wider flex items-center gap-1">
          <Icon className="w-3 h-3"/> {label}
        </span>
        <span className="text-sm font-bold tabular-nums">{used} <span className="text-fg-muted font-normal">/ {max}</span></span>
      </div>
      <div className="h-2 bg-bg-secondary rounded-full overflow-hidden">
        <div className={`h-full ${colorClass} transition-all`}
             style={{ width: `${Math.min(pct, 100)}%` }}
             data-testid={`${testid}-bar`}/>
      </div>
      <div className="text-[11px] text-fg-muted mt-1">{pct}%</div>
    </div>
  );
}

// Iter 89 — Click a sparkline bar to see who logged in that day.
function MauSparkline({ breakdown, sparkMax }) {
  const [activeDay, setActiveDay] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const openDay = async (day) => {
    if (activeDay === day) { setActiveDay(null); setDetail(null); return; }
    setActiveDay(day); setDetail(null); setLoading(true);
    try {
      const { data } = await api.get(`/clinic/me/usage/day/${day}`);
      setDetail(data);
    } catch (_e) { setDetail({ active_users: [], day }); }
    finally { setLoading(false); }
  };
  return (
    <div className="relative" data-testid="usage-mau-sparkline">
      <div className="flex items-end gap-0.5 h-10">
        {breakdown.map((d) => (
          <button key={d.day}
                  onClick={() => openDay(d.day)}
                  title={`${d.day}: ${d.count} active user${d.count === 1 ? '' : 's'} — click to see who`}
                  className={`flex-1 transition-all min-h-[2px] cursor-pointer ${
                    activeDay === d.day
                      ? 'bg-brand'
                      : 'bg-brand/60 hover:bg-brand hover:scale-y-110 origin-bottom'
                  }`}
                  style={{ height: `${Math.round((d.count / sparkMax) * 100)}%` }}
                  data-testid={`mau-bar-${d.day}`}/>
        ))}
      </div>
      {activeDay && (
        <div className="mt-2 p-3 bg-bg-secondary/60 border border-soft rounded-md text-xs space-y-1.5"
             data-testid="mau-day-detail">
          <div className="flex items-center justify-between font-semibold">
            <span>{new Date(activeDay).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
            <button onClick={() => { setActiveDay(null); setDetail(null); }}
                    className="text-fg-muted hover:text-fg" data-testid="mau-day-close">✕</button>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-fg-muted"><Loader2 className="w-3 h-3 animate-spin"/> Loading…</div>
          ) : detail?.active_users?.length ? (
            detail.active_users.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between" data-testid={`mau-day-user-${u.user_id}`}>
                <div>
                  <span className="font-semibold">{u.name}</span>
                  <span className="text-fg-muted ml-2">{u.role}</span>
                </div>
                <span className="text-fg-muted tabular-nums">{u.logins} login{u.logins === 1 ? '' : 's'}</span>
              </div>
            ))
          ) : (
            <p className="text-fg-muted">No activity that day.</p>
          )}
        </div>
      )}
    </div>
  );
}




function PrivacyFirstScorecard({ trust }) {
  const earned = trust.earned;
  const totalCriteria = trust.criteria?.length || 5;
  const metCount = (trust.criteria || []).filter(c => c.met).length;
  return (
    <div className={`bento-card ${earned ? 'border-2 border-[#5DBB66]/40' : 'border-2 border-[#F2B100]/40'}`} data-testid="privacy-first-scorecard">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-[#1B5E20]"/>
          <h2 className="font-heading font-semibold">Privacy-First scorecard</h2>
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${earned ? 'bg-[#1B5E20]/15 text-[#1B5E20]' : 'bg-[#F2B100]/15 text-[#7A4F00]'}`} data-testid="pf-score-badge">
            {trust.score}/100 {earned && '· EARNED'}
          </span>
        </div>
        <p className="text-xs text-fg-muted">Meet 4 of 5 criteria to earn the public Privacy-First badge on your clinic page.</p>
      </div>
      {trust.data_residency && (
        <div className="bg-[#FAF6EE] border border-soft rounded-md px-3 py-2 mb-3 text-xs inline-flex items-center gap-2" data-testid="billing-residency-badge">
          <Globe2 className="w-3.5 h-3.5 text-brand"/>
          <span><strong>Data residency:</strong> {RESIDENCY_FULL[trust.data_residency] || trust.data_residency.toUpperCase()}</span>
          <span className="text-fg-muted">— sticky after signup</span>
        </div>
      )}
      <ul className="space-y-2.5" data-testid="pf-criteria-list">
        {trust.criteria.map(c => (
          <li key={c.key} className="flex items-start justify-between gap-3" data-testid={`pf-criterion-${c.key}`}>
            <div className="flex items-start gap-2 flex-1">
              {c.met
                ? <CheckCircle2 className="w-4 h-4 text-[#1B5E20] shrink-0 mt-0.5"/>
                : <XCircle className="w-4 h-4 text-[#C62828] shrink-0 mt-0.5"/>}
              <span className={`text-sm ${c.met ? '' : 'text-fg-muted'}`}>{c.label}</span>
            </div>
            {!c.met && c.fix && (
              <a href={c.fix.url || `/admin/settings#${c.fix.settings_anchor}`}
                  className="text-xs text-brand hover:underline shrink-0"
                  data-testid={`pf-fix-${c.key}`}>
                Fix this →
              </a>
            )}
          </li>
        ))}
      </ul>
      <div className="text-xs text-fg-muted mt-3 pt-3 border-t border-soft">
        {metCount}/{totalCriteria} criteria met. {earned
          ? 'Your public clinic page shows the Privacy-First badge — a measurable conversion lift in EU markets.'
          : 'Fix the missing items above to earn the badge on your public clinic page.'}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────
 * Iter 99 — Anonymised peer-benchmarks card.
 * ──────────────────────────────────────────────────────────────────── */
function BenchmarksCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get('/clinic/me/benchmarks')
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || ''));
  }, []);
  if (err) return null;
  if (!data) return <div className="bento-card text-sm text-fg-muted" data-testid="benchmarks-loading">Loading peer benchmarks…</div>;
  if (!data.available) {
    return (
      <div className="bento-card" data-testid="benchmarks-unavailable">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="w-5 h-5 text-brand"/>
          <h2 className="font-heading font-semibold">Peer benchmarks</h2>
        </div>
        <p className="text-xs text-fg-muted">{data.reason}</p>
      </div>
    );
  }
  const METRICS = [
    { key: 'revenue_per_visit',     label: 'Revenue / visit',  suffix: '€' },
    { key: 'mau',                   label: 'Active patients (30d)' },
    { key: 'completed_visits_30d',  label: 'Visits delivered (30d)' },
    { key: 'no_show_pct',           label: 'No-show rate',     suffix: '%' },
    { key: 'retention_pct',         label: 'Patient retention', suffix: '%' },
    { key: 'revenue_30d',           label: 'Revenue (30d)',    suffix: '€' },
  ];
  const verdictColor = v => v === 'good' ? 'text-[#1B5E20]' : (v === 'watch' ? 'text-[#C62828]' : 'text-fg-muted');
  const verdictLabel = v => v === 'good' ? '🟢 top quartile' : (v === 'watch' ? '🔴 below median' : '⚪ on par');
  return (
    <div className="bento-card" data-testid="benchmarks-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-brand"/>
          <h2 className="font-heading font-semibold">Peer benchmarks</h2>
          <span className="text-[10px] uppercase tracking-wider bg-brand/15 text-brand px-2 py-0.5 rounded font-semibold">{data.tier}-tier · {data.sample_size} peers</span>
        </div>
        <p className="text-xs text-fg-muted">Anonymised — no clinic identifiable, k-anonymity ≥ 3.</p>
      </div>
      <table className="w-full text-sm" data-testid="benchmarks-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-fg-muted border-b border-soft">
            <th className="py-2">Metric</th>
            <th className="py-2 text-right">You</th>
            <th className="py-2 text-right">Median</th>
            <th className="py-2 text-right">Top 25%</th>
            <th className="py-2 text-right">Verdict</th>
          </tr>
        </thead>
        <tbody>
          {METRICS.map(m => (
            <tr key={m.key} className="border-b border-soft/60" data-testid={`bench-row-${m.key}`}>
              <td className="py-2">{m.label}</td>
              <td className="py-2 text-right font-semibold">{data.my[m.key]}{m.suffix || ''}</td>
              <td className="py-2 text-right text-fg-muted">{data.peer_median[m.key]}{m.suffix || ''}</td>
              <td className="py-2 text-right text-fg-muted">{data.peer_top_quartile[m.key]}{m.suffix || ''}</td>
              <td className={`py-2 text-right text-xs ${verdictColor(data.verdicts[m.key])}`} data-testid={`bench-verdict-${m.key}`}>{verdictLabel(data.verdicts[m.key])}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-fg-muted mt-3">
        Updated daily. Compare yourself against {data.sample_size} active clinics on the same plan.
      </div>
    </div>
  );
}
